import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
// @ts-ignore
import { Component, OnInit } from '@angular/core';
// @ts-ignore
import { CommonModule } from '@angular/common';
// @ts-ignore
import { FormsModule } from '@angular/forms';
// @ts-ignore
import { firstValueFrom } from 'rxjs';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';

interface ParametroLinha {
  codigo:       number;
  nome:         string;
  valor:        string;
  descricao:    string;
  ativo:        string;
  dataCriacao:  string;
  codusu:       number;
  nomeUsuario:  string;
}

interface EtiquetaConfigLinha {
  codconfig: number;
  nome:      string;
}

/** Telas que suportam restringir quais etiquetas cadastradas aparecem nelas.
 *  Pra liberar uma nova tela, basta adicionar uma entrada aqui — o nome do
 *  parâmetro (chave) é o que fica gravado em GPL_PARAMETRO como CSV de
 *  CODCONFIG liberados (ex.: "1,3"). Quem lê esse parâmetro hoje: o modal
 *  de Dados do Transporte (gateway.component.ts). */
interface TelaComEtiquetas {
  chave: string;
  label: string;
}

// Tela genérica de parametrização (NOME/VALOR) — pedido do usuário pra não
// deixar mais regras "fixas no código". Uso conhecido hoje:
//   ALTCOT = dias — ao abrir um pedido de venda pra edição com Data de
//            Emissão mais antiga que isso, a tela pergunta se atualiza o
//            preço de venda dos itens (ver pedido-venda.component.ts).
//   FABNAC = códigos de fabricante (CODMARCA), separados por vírgula,
//            tratados como "nacional" no cálculo inverso R$→U$ da tela de
//            Venda — antes fixo em 70 (Embraer).
// @ts-ignore
@Component({
  selector: 'app-parametro',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './parametro.component.html',
  styleUrl: './parametro.component.css'
})
export class ParametroComponent implements OnInit {

  // ── Lista ─────────────────────────────────────────────────
  carregando = false;
  parametros: ParametroLinha[] = [];
  erro = '';
  sucesso = '';

  // ── Opções do dicionário ───────────────────────────────────
  opcoesAtivo: string[] = [];

  // ── Dropdown de ações ─────────────────────────────────────
  dropdownAberto: number | null = null;

  // ── Modal de formulário ───────────────────────────────────
  modalAberto = false;
  salvando    = false;
  modoEdicao  = false;
  form = { codigo: 0, nome: '', valor: '', descricao: '', ativo: '' };
  erroForm = '';

  // ── Etiquetas por tela ─────────────────────────────────────
  readonly telasComEtiquetas: TelaComEtiquetas[] = [
    { chave: 'ETIQUETAS_TELA_TRANSPORTE', label: 'Dados de Transporte (lista de pedidos → Ações)' }
  ];
  telaEtiquetaSelecionada = this.telasComEtiquetas[0].chave;
  etiquetasCadastradas: EtiquetaConfigLinha[] = [];
  private etiquetasMarcadasPorTela: Record<string, Set<number>> = {};
  salvandoEtiquetasTela = false;
  erroEtiquetasTela = '';
  sucessoEtiquetasTela = '';

  constructor(private readonly gateway: SankhyaGatewayService) {}

  ngOnInit(): void {
    this.carregar();
  }

  async carregar(): Promise<void> {
    this.carregando = true;
    this.erro = '';
    try {
      const [resLista, resOpcoes, resEtiquetas] = await Promise.all([
        firstValueFrom(this.gateway.listarParametros()),
        firstValueFrom(this.gateway.buscarOpcoesParametro()),
        firstValueFrom(this.gateway.listarEtiquetaConfigs())
      ]);

      const bodyLista: any     = (resLista as any)?.responseBody ?? resLista;
      const bodyOpcoes: any    = (resOpcoes as any)?.responseBody ?? resOpcoes;
      const bodyEtiquetas: any = (resEtiquetas as any)?.responseBody ?? resEtiquetas;

      this.parametros  = bodyLista?.lista ?? [];
      this.opcoesAtivo = bodyOpcoes?.opcoesAtivo ?? [];
      this.etiquetasCadastradas = (bodyEtiquetas?.lista ?? []).map((c: any) => ({
        codconfig: Number(c.codconfig),
        nome:      c.nome
      }));

      this.etiquetasMarcadasPorTela = {};
      for (const tela of this.telasComEtiquetas) {
        const valor = this.parametros.find(p => p.nome === tela.chave)?.valor ?? '';
        this.etiquetasMarcadasPorTela[tela.chave] = new Set(
          valor.split(',').map(v => Number(v.trim())).filter(v => Number.isFinite(v) && v > 0)
        );
      }
    } catch (e: any) {
      this.erro = e?.message ?? 'Erro ao carregar dados.';
    } finally {
      this.carregando = false;
    }
  }

  // ── Etiquetas por tela ─────────────────────────────────────
  etiquetaMarcada(codconfig: number): boolean {
    return this.etiquetasMarcadasPorTela[this.telaEtiquetaSelecionada]?.has(codconfig) ?? false;
  }

  toggleEtiquetaMarcada(codconfig: number): void {
    const marcadas = this.etiquetasMarcadasPorTela[this.telaEtiquetaSelecionada]
      ??= new Set<number>();
    if (marcadas.has(codconfig)) marcadas.delete(codconfig);
    else marcadas.add(codconfig);
  }

  async salvarEtiquetasTela(): Promise<void> {
    this.erroEtiquetasTela = '';
    this.salvandoEtiquetasTela = true;
    try {
      const tela = this.telasComEtiquetas.find(t => t.chave === this.telaEtiquetaSelecionada)!;
      const marcadas = this.etiquetasMarcadasPorTela[tela.chave] ?? new Set<number>();
      const csv = [...marcadas].sort((a, b) => a - b).join(',');
      const existente = this.parametros.find(p => p.nome === tela.chave);

      const payload: any = {
        nome:      tela.chave,
        valor:     csv,
        descricao: `Etiquetas liberadas na tela: ${tela.label}`,
        // Fixo em 'SIM' (não vem de opcoesAtivo): o backend filtra
        // GPL_PARAMETRO por "ATIVO = 'SIM'" (literal, case-sensitive) em
        // ParametroRepository.buscarValores — é isso que
        // buscarValoresParametros() usa pra ler este parâmetro na tela de
        // Dados do Transporte. Ver EtiquetaConfigLinha/telasComEtiquetas acima.
        ativo:     'SIM'
      };
      if (existente) payload.codigo = existente.codigo;

      const res: any = await firstValueFrom(this.gateway.salvarParametro(payload));
      const body = res?.responseBody ?? res;
      if (body?.sucesso === true || body?.sucesso === 'true') {
        this.sucessoEtiquetasTela = 'Etiquetas da tela atualizadas.';
        await this.carregar();
        setTimeout(() => this.sucessoEtiquetasTela = '', 3500);
      } else {
        this.erroEtiquetasTela = body?.erro ?? 'Erro ao salvar.';
      }
    } catch (e: any) {
      this.erroEtiquetasTela = e?.message ?? 'Erro de comunicação.';
    } finally {
      this.salvandoEtiquetasTela = false;
    }
  }

  // ── Dropdown ──────────────────────────────────────────────
  // Posição calculada na hora de abrir (a partir do botão "⋮" clicado) —
  // o menu usa position:fixed (ver CSS .ds-dropdown-menu--flutuante) pra
  // sair por cima da tabela em vez de ficar cortado pelo overflow:auto do
  // ds-table-container (linha de baixo da tabela cortava o "Editar").
  dropdownPos: { top: number; right: number } | null = null;

  toggleDropdown(event: Event, codigo: number): void {
    event.stopPropagation();
    if (this.dropdownAberto === codigo) {
      this.dropdownAberto = null;
      this.dropdownPos = null;
      return;
    }
    this.dropdownAberto = codigo;
    const botao = event.currentTarget as HTMLElement;
    const rect = botao.getBoundingClientRect();
    this.dropdownPos = {
      top: rect.bottom + 6,
      right: window.innerWidth - rect.right,
    };
  }

  fecharDropdown(): void {
    this.dropdownAberto = null;
    this.dropdownPos = null;
  }

  // ── Modal novo / editar ───────────────────────────────────
  abrirNovo(): void {
    this.modoEdicao = false;
    this.form = {
      codigo: 0,
      nome: '',
      valor: '',
      descricao: '',
      ativo: this.opcoesAtivo[this.opcoesAtivo.length - 1] ?? 'SIM'
    };
    this.erroForm = '';
    this.modalAberto = true;
  }

  abrirEditar(p: ParametroLinha): void {
    this.modoEdicao = true;
    this.form = { codigo: p.codigo, nome: p.nome, valor: p.valor, descricao: p.descricao, ativo: p.ativo };
    this.erroForm = '';
    this.modalAberto = true;
    this.fecharDropdown();
  }

  fecharModal(): void {
    this.modalAberto = false;
  }

  async salvar(): Promise<void> {
    this.erroForm = '';
    if (!this.form.nome.trim())  { this.erroForm = 'Informe o nome do parâmetro.'; return; }
    if (!this.form.valor.trim()) { this.erroForm = 'Informe o valor.'; return; }
    if (!this.form.ativo)        { this.erroForm = 'Selecione o ativo.'; return; }

    this.salvando = true;
    try {
      const payload: any = {
        nome:      this.form.nome.trim(),
        valor:     this.form.valor.trim(),
        descricao: this.form.descricao.trim(),
        ativo:     this.form.ativo
      };
      if (this.modoEdicao) payload.codigo = this.form.codigo;

      const res: any = await firstValueFrom(this.gateway.salvarParametro(payload));
      const body = res?.responseBody ?? res;

      if (body?.sucesso === true || body?.sucesso === 'true') {
        this.sucesso = body.mensagem ?? 'Salvo com sucesso.';
        this.modalAberto = false;
        await this.carregar();
        setTimeout(() => this.sucesso = '', 3500);
      } else {
        this.erroForm = body?.erro ?? 'Erro ao salvar.';
      }
    } catch (e: any) {
      this.erroForm = e?.message ?? 'Erro de comunicação.';
    } finally {
      this.salvando = false;
    }
  }

  // ── Alterar ativo ─────────────────────────────────────────
  alterando: number | null = null;

  async alterarAtivo(p: ParametroLinha, novoAtivo: string): Promise<void> {
    this.fecharDropdown();
    this.alterando = p.codigo;
    this.erro = '';
    try {
      const res: any = await firstValueFrom(
        this.gateway.alterarAtivoParametro(p.codigo, novoAtivo)
      );
      const body = res?.responseBody ?? res;
      if (body?.sucesso === true || body?.sucesso === 'true') {
        this.sucesso = body.mensagem ?? 'Situação atualizada.';
        await this.carregar();
        setTimeout(() => this.sucesso = '', 3500);
      } else {
        this.erro = body?.erro ?? 'Erro ao alterar situação.';
      }
    } catch (e: any) {
      this.erro = e?.message ?? 'Erro de comunicação.';
    } finally {
      this.alterando = null;
    }
  }

  // ── Helpers ───────────────────────────────────────────────
  ativoClass(ativo: string): string {
    // Classes do design system (design-system.css) — ds-badge já vem no
    // template, aqui só o modificador de cor.
    const v = (ativo ?? '').toLowerCase();
    if (v === 'sim') return 'ds-badge--success';
    return '';
  }

  labelAtivo(ativo: string): string {
    return ativo || '—';
  }

  proximoAtivo(p: ParametroLinha): string {
    if (this.opcoesAtivo.length < 2) return '';
    const idx = this.opcoesAtivo.indexOf(p.ativo);
    return idx >= 0 ? this.opcoesAtivo[(idx + 1) % this.opcoesAtivo.length] : this.opcoesAtivo[0];
  }
}
